import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, makeStyles } from "@material-ui/core";
import CircularProgress from "views/components/CircularProgress/CircularProgress";
import { apiHandler } from "assets/api";
import { endpoint } from "assets/api/endpoint";
import { useSelector } from "react-redux";
import SubHeader from "components/UpdateUi/SubHeader/SubHeader";
import Button from "../../components/CustomButtons/Button";
import CustomTextField from "components/UpdateUi/CustomeTextField";
import CustomSelect from "components/UpdateUi/CustomeSelect";
import { toCamelCase } from "utils/Utils";
import brandTheme from "brandTheme/Theme";
import { accountTypeList } from "assets/properties/properties";
import ToastModal from "views/components/ToastModal";
import { normalizeName } from "utils/Utils";
import BottomButton from "components/UpdateUi/BottomButton/BottomButton";
import { isMaskedBankAccount } from "utils/Utils";

const useStyles = makeStyles((theme) => ({
  container: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    width: "92%",
    margin: "0 auto",
    minHeight: "85vh",
    overflow: "hidden",
  },
  inputFieldContainer: {
    margin: "0 auto",
    maxWidth: "1100px",
  },
  fieldContainer: {
    margin: "2% 2% 0px 0px",
    maxWidth: "29.3%",
    "@media (max-width: 768px)": {
      maxWidth: "46%",
      margin: "5% 2% 0px 2%",
    },
    "@media (max-width: 600px)": {
      maxWidth: "100%",
    },
    "@media (max-width: 450px)": {
      margin: "10% 2% 0px 2%",
    },
  },
  verifyButton: {
    position: "absolute",
    top: 0,
    right: 0,
    margin: 0,
  },
  nextButton: {
    boxShadow: "inherit",
    color: brandTheme.white,
    padding: "0px 40px",
    height: "55px",
    width: "8%",
    textTransform: "capitalize",
    background: "linear-gradient(135deg, #15254F 0%, #1CB167 50%, #28D17F 100%)",
    "&:hover": {
          background: "linear-gradient(135deg, #15254F 0%, #1CB167 50%, #28D17F 100%)",
    },
    "&:focus": {
          background: "linear-gradient(135deg, #15254F 0%, #1CB167 50%, #28D17F 100%)",
    },
  },
}));

const BankDetails = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { leadId } = useSelector((state) => state.login.userCurrentLead);
  const { mobileNumber, name } = useSelector((state) => state.login.userData);
  const { authToken } = useSelector((state) => state.login.authData);

  const [ifscCode, setIfscCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [branch, setBranch] = useState("");
  const [address, setAddress] = useState("");
  const [stateName, setStateName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [status, setStatus] = useState("");
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [accountType, setAccountType] = useState(accountTypeList[0]?.value);
  const [callInProgress, setCallInProgress] = useState(false);
  const [isValidatedByAPI, setIsValidatedByAPI] = useState(false);
  const [matchinPercentage, setMatchingPercentage] = useState(null);
  const [verified, setVerified] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [applicantName, setApplicantName] = useState("");
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState("");
  const [maskedAccountNumber, setMaskedAccountNumber] = useState("");
  const [isBankAccountMasked, setIsBankAccountMasked] = useState(false);
  const isAccountValidationRequired =
  isBankAccountMasked || verified === false;
  const isMaskedAccountMatched = useMemo(() => {
  if (!isBankAccountMasked || !maskedAccountNumber || !accountNumber) {
    return false;
  }

  const maskedLastFour = maskedAccountNumber.slice(-4);
  const enteredLastFour = accountNumber.slice(-4);

  return maskedLastFour === enteredLastFour;
}, [isBankAccountMasked, maskedAccountNumber, accountNumber]);
  const toastUpdate = (type, message, showToast) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
  };

  useEffect(() => {
    getBankDetails();
  }, [leadId]);

  const getBankDetails = async () => {
    setCallInProgress(true);

    const result = await apiHandler({
      url: endpoint.USER_BANK_DETAILS_FIND_API + leadId,
      method: "GET",
      authToken,
    });
    setCallInProgress(false);
    const data = result?.data;
    if (data?.bankAccountNo) {
      setIfscCode(data.ifscCode);
      setBankName(data.bankName);
      setBranch(data.branch);
      setAddress(data.branchAddress);
      setStateName(data.state);
      const masked = isMaskedBankAccount(data.bankAccountNo);
      setIsBankAccountMasked(masked);
      if (masked) {
        setMaskedAccountNumber(data.bankAccountNo);
        setAccountNumber("");
      } else {
        setMaskedAccountNumber("");
        setAccountNumber(data.bankAccountNo);
      }
      setStatus(data.status);
      setBeneficiaryName(data.beneficiaryName);
      setAccountType(data.accountType?.toLowerCase());
      setMatchingPercentage(data.matchingPercentage);
      setMatchResult(data.matchResult);
      setVerified(data.verified);
      setIsValidatedByAPI(false);
    } else {
      fetchLoanSummary();
    }
  };
  const fetchLoanSummary = useCallback(async () => {
    setCallInProgress(true);
    const result = await apiHandler({
      url: endpoint.USER_DATA_MOBILE_NUMBER_API + mobileNumber,
      method: "GET",
      authToken,
    });
    setCallInProgress(false);
    if(result?.data?.[0]?.applicantName){
      setApplicantName(result?.data?.[0]?.applicantName);
    }
    const data = result?.data?.[0]?.bankDetailResponse;
    if (data?.ifscCode) {
      setIfscCode(data.ifscCode);
      setBankName(data.bankName);
      setBranch(data.branch);
      setAddress(data.address);
      setStateName(data.stateName);
      setAccountNumber(data.accountNumber);
      setStatus(data.status);
      setBeneficiaryName(data.beneficiaryName);
      let accountTypeName = data?.accountType;
      if (accountTypeName) {
        const normalized = accountTypeName.toLowerCase();
        if (normalized === "saving" || normalized === "savings") {
          accountTypeName = "savings";
        }
      }
      setAccountType(accountTypeName);
    }
  }, [authToken, mobileNumber]);

  const ifscCodeHandler = useCallback(async () => {
    if (!ifscCode) return;
    setCallInProgress(true);
    const res = await apiHandler({
      url: endpoint.IFSC_CODE_VERIFIED_API + ifscCode.toUpperCase(),
      authToken,
    });
    setCallInProgress(false);
    const data = res.data;
    if (data?.error_code) {
      toastUpdate("Error", "Please fill the correct IFSC code", true);
    } else {
      setBankName(data.BANK);
      setBranch(data.BRANCH);
      setAddress(data.ADDRESS);
      setStateName(data.STATE);
    }
  }, [ifscCode, authToken]);

  const AccountValidater = useCallback(async () => {
     if (!accountNumber || !ifscCode) return;

  if (isBankAccountMasked && !isMaskedAccountMatched) {
    toastUpdate(
      "Error",
      "The last 4 digits of the bank account number do not match.",
      true
    );
    return;
  }
    setCallInProgress(true);
    const res = await apiHandler({
      url: endpoint.USER_BANK_VALIDATE_API,
      method: "POST",
      authToken,
      data: {
        beneficiaryName: name,
        ifsc: ifscCode.toUpperCase(),
        accountNo: accountNumber,
        mobileNumber,
      },
    });
    setCallInProgress(false);
    if (res?.data?.error_code) {
      toastUpdate("Error", res?.data?.user_description, true);
    } else {
      setStatus(res.data.status);
      setBeneficiaryName(res.data.beneficiaryName);
      setMatchingPercentage(res?.data?.matchingPercentage);
      setVerified(res?.data?.verified);
      setMatchResult(res?.data?.matchResult);
      setIsValidatedByAPI(true); 
    }
  }, [
    ifscCode,
    accountNumber,
    authToken,
    name,
    mobileNumber,
    isBankAccountMasked,
    isMaskedAccountMatched,
  ]);

  const handleNext = async () => {
    if (isBankAccountMasked && !isMaskedAccountMatched) {
      toastUpdate(
        "Error",
        "The last 4 digits of the bank account number do not match.",
        true
      );
      return;
    }
    if (isAccountValidationRequired && !isValidatedByAPI) {
      toastUpdate(
        "Error",
        "Please validate the ifsc and bank account before proceeding.",
        true
      );
      return;
    }
    setCallInProgress(true);
    const res = await apiHandler({
      url: endpoint.USER_BANK_DETAILS_CREATE_API + leadId,
      method: "POST",
      authToken,
      data: {
        accountNo: accountNumber,
        ifsc: ifscCode.toUpperCase(),
        beneficiaryName,
        accountType,
        bankName,
        branchAddress: address,
        branch,
        state: stateName,
        status,
        matchingPercentage: matchinPercentage,
        verified,
        matchResult,
      },
    });
    setCallInProgress(false);
    if (res?.data?.error_code) {
      toastUpdate("Error", res?.data?.user_description, true);
    } else {
      navigate("/journey/SanctionLetter");
    }
  };

  const isIFSCVerified = useMemo(
    () => bankName && branch && address && stateName,
    [bankName, branch, address, stateName]
  );
  const isAccountValidated = useMemo(
    () => status && beneficiaryName,
    [status, beneficiaryName]
  );

  const isFormValid = useMemo(() => {
    return (
      ifscCode?.trim() &&
      accountNumber?.trim() &&
      beneficiaryName?.trim() &&
      bankName?.trim() &&
      branch?.trim() &&
      accountType?.trim() &&
      status?.trim()
    );
  }, [
    ifscCode,
    accountNumber,
    beneficiaryName,
    bankName,
    branch,
    address,
    stateName,
    accountType,
    status,
  ]);
  
  return (
    <Grid container className={classes.container}>
      <Grid item xs={12}>
        <SubHeader
          label="Bank Details"
        />
      </Grid>

      {/* IFSC Section */}
      <Grid item xs={12}>
        <Grid container className={classes.inputFieldContainer}>
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            className={classes.fieldContainer}
            style={{ position: "relative" }}
          >
            <CustomTextField
              label="IFSC Code"
              placeholder="IFSC Code"
              value={ifscCode?.toUpperCase()}
              onChange={(e) => setIfscCode(e.target.value)}
              disabled={isIFSCVerified}
            />
            {!isValidatedByAPI && (
              <Button
                className={`${classes.verifyButton} ${classes.nextButton}`}
                size="small"
                onClick={ifscCodeHandler}
                disabled={isIFSCVerified}
              >
                Search
              </Button>
              )}
          </Grid>

          <Grid item xs={12} sm={6} md={4} className={classes.fieldContainer}>
            <CustomTextField
              label="Bank Name"
              value={toCamelCase(bankName)}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} className={classes.fieldContainer}>
            <CustomTextField
              label="Branch"
              value={toCamelCase(branch)}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} className={classes.fieldContainer}>
            <CustomTextField
              label="Address"
              value={toCamelCase(address)}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} className={classes.fieldContainer}>
            <CustomTextField
              label="State"
              value={toCamelCase(stateName)}
              disabled
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Account Validation Section */}
      <Grid item xs={12}>
        <Grid container className={classes.inputFieldContainer}>
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            className={classes.fieldContainer}
            style={{ position: "relative" }}
          >
            <CustomTextField
              label={isBankAccountMasked ? "Enter Bank Account Number" : "Bank Account"}
              placeholder={
                isBankAccountMasked
                  ? "Enter bank account number"
                  : "Bank Account"
              }
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              type="number"
            />
            {isAccountValidationRequired && !isValidatedByAPI && (
              <Button
                className={`${classes.verifyButton} ${classes.nextButton}`}
                size="small"
                onClick={AccountValidater}
                disabled={!isIFSCVerified || !accountNumber}
              >
                Validate
              </Button>
            )}
          </Grid>
          {isBankAccountMasked && (
            <Grid item xs={12} sm={6} md={4} className={classes.fieldContainer}>
              <CustomTextField
                label="Bank Account"
                value={maskedAccountNumber}
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4} className={classes.fieldContainer}>
            <CustomTextField
              label="Status"
              value={toCamelCase(status)}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} className={classes.fieldContainer}>
            <CustomTextField
              label="Beneficiary Name"
              value={toCamelCase(beneficiaryName)}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4} className={classes.fieldContainer}>
            <CustomSelect
              label="Account Type"
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              options={accountTypeList}
              // disabled={isAccountValidated}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <BottomButton
          button1="Next"
          button1click={handleNext}
          disabled1={!isFormValid}
        />
      </Grid>
      {callInProgress && <CircularProgress callInProgress />}
      {toast && (
        <ToastModal
          toastMessage={toastMessage}
          toastMessageType={toastMessageType}
          toastUpdate={toastUpdate}
        />
      )}
    </Grid>
  );
};

export default BankDetails;
